import { useState, useEffect, useRef } from "react";
import { MicrophoneModal } from "./modal/MicrophoneModal";
import { RefreshWarningModal } from "./modal/RefreshWarningModal";
import { formatAudioTimestamp } from "../utils/AudioUtils";
import { webmFixDuration } from "../utils/BlobFix";
import { AudioVisualizer } from "./AudioVisualizer";
import { audioChunkManager } from "../utils/AudioChunkManager";
import { v4 as uuidv4 } from "uuid";

// Audio context configuration
const AUDIO_CONFIG = {
    fftSize: 2048,
    minDecibels: -70,
    maxDecibels: -30,
    smoothingTimeConstant: 0.5
};

interface AudioContextState {
    context: AudioContext | null;
    analyzer: AnalyserNode | null;
    gainNode: GainNode | null;
    source: MediaStreamAudioSourceNode | null;
}

function getMimeType() {
    const types = [
        "audio/webm",
        "audio/mp4",
        "audio/ogg",
        "audio/wav",
        "audio/aac",
    ];
    for (let i = 0; i < types.length; i++) {
        if (MediaRecorder.isTypeSupported(types[i])) {
            return types[i];
        }
    }
    return undefined;
}

export default function AudioRecorder(props: {
    onRecordingComplete: (blob: Blob) => void;
}) {
    const [recording, setRecording] = useState(false);
    const [duration, setDuration] = useState(0);
    const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
    const [micPermission, setMicPermission] = useState<boolean | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isInitializing, setIsInitializing] = useState(true);
    const [showMicModal, setShowMicModal] = useState(true);
    const [showRefreshWarning, setShowRefreshWarning] = useState(false);
    const [isVisualizerReady, setIsVisualizerReady] = useState(false);
    const [visualizerError, setVisualizerError] = useState<string | null>(null);
    const [streamStatus, setStreamStatus] = useState<'inactive' | 'active' | 'error'>('inactive');
    const [sessionId] = useState(() => uuidv4());
    const [isButtonBusy, setIsButtonBusy] = useState(false);
    const [processingState, setProcessingState] = useState<'idle' | 'processing' | 'complete'>('idle');
    const [audioLevel, setAudioLevel] = useState(0);

    const streamRef = useRef<MediaStream | null>(null);
    const refreshConfirmedRef = useRef(false);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const audioContextStateRef = useRef<AudioContextState>({
        context: null,
        analyzer: null,
        gainNode: null,
        source: null
    });
    const timerRef = useRef<number | null>(null);
    const audioLevelIntervalRef = useRef<number | null>(null);

    const initializeMic = async () => {
        setIsInitializing(true);
        try {
            await navigator.mediaDevices.getUserMedia({ audio: true });
            setMicPermission(true);
            setShowMicModal(false);
        } catch (err) {
            setMicPermission(false);
            setError("Microphone access denied. Please enable it in your browser settings.");
            console.error("Mic permission error:", err);
        } finally {
            setIsInitializing(false);
        }
    };

    // Check microphone permission on mount
    useEffect(() => {
        initializeMic();
    }, []);

    // Clean up audio context and nodes
    const cleanupAudioContext = () => {
        const { source, gainNode, analyzer, context } = audioContextStateRef.current;
        
        if (source) {
            source.disconnect();
            audioContextStateRef.current.source = null;
        }
        
        if (gainNode) {
            gainNode.disconnect();
            audioContextStateRef.current.gainNode = null;
        }
        
        if (analyzer) {
            analyzer.disconnect();
            audioContextStateRef.current.analyzer = null;
        }
        
        if (context) {
            if (context.state !== 'closed') {
                context.close();
            }
            audioContextStateRef.current.context = null;
        }

        if (audioLevelIntervalRef.current) {
            clearInterval(audioLevelIntervalRef.current);
            audioLevelIntervalRef.current = null;
        }
    };

    // Audio level monitoring
    const startAudioLevelMonitoring = () => {
        if (audioLevelIntervalRef.current) {
            clearInterval(audioLevelIntervalRef.current);
        }
        
        const analyzer = audioContextStateRef.current.analyzer;
        if (!analyzer) return;
        
        const dataArray = new Uint8Array(analyzer.frequencyBinCount);
        
        audioLevelIntervalRef.current = window.setInterval(() => {
            analyzer.getByteFrequencyData(dataArray);
            
            // Calculate average volume level
            let sum = 0;
            for (let i = 0; i < dataArray.length; i++) {
                sum += dataArray[i];
            }
            const avg = sum / dataArray.length;
            
            // Normalize to 0-100 range
            const level = Math.min(100, Math.max(0, (avg / 128) * 100));
            setAudioLevel(level);
        }, 100);
    };

    // Initialize AudioContext and nodes
    const initializeAudioContext = async (stream: MediaStream) => {
        try {
            cleanupAudioContext();
            
            const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
            const audioContext = new AudioContext();
            
            const analyzer = audioContext.createAnalyser();
            analyzer.fftSize = AUDIO_CONFIG.fftSize;
            analyzer.minDecibels = AUDIO_CONFIG.minDecibels;
            analyzer.maxDecibels = AUDIO_CONFIG.maxDecibels;
            analyzer.smoothingTimeConstant = AUDIO_CONFIG.smoothingTimeConstant;
            
            const gainNode = audioContext.createGain();
            gainNode.gain.value = 1;
            
            const source = audioContext.createMediaStreamSource(stream);
            
            source.connect(gainNode);
            gainNode.connect(analyzer);
            
            audioContextStateRef.current = {
                context: audioContext,
                analyzer,
                gainNode,
                source
            };
            
            setIsVisualizerReady(true);
            setStreamStatus('active');
            
            // Start monitoring audio levels
            startAudioLevelMonitoring();
            
        } catch (err) {
            console.error("Failed to initialize audio context:", err);
            setVisualizerError("Failed to initialize audio visualizer");
            setStreamStatus('error');
        }
    };

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (recording && !refreshConfirmedRef.current) {
                e.preventDefault();
                e.returnValue = "You have an active recording. Are you sure you want to leave?";
                setShowRefreshWarning(true);
                return e.returnValue;
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [recording]);

    const handleRefreshConfirm = () => {
        refreshConfirmedRef.current = true;
        window.location.reload();
    };

    useEffect(() => {
        const checkExistingSession = async () => {
            try {
                const state = audioChunkManager.getSessionState();
                
                if (state.isRecording) {
                    const session = await audioChunkManager.getCurrentSession();
                    if (session) {
                        setDuration(Math.floor((Date.now() - session.startTime) / 1000));
                    }
                }
            } catch (error) {
                console.error("Error checking for previous recording session:", error);
            }
        };
        
        checkExistingSession();
    }, [sessionId]);

    const startRecording = async () => {
        try {
            setIsButtonBusy(true);
            setError(null);
            setProcessingState('idle');
            
            // Get user media
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            
            // Initialize AudioContext and visualizer
            await initializeAudioContext(stream);
            
            // Get MIME type for recording
            const mimeType = getMimeType();
            
            // Create MediaRecorder
            const mediaRecorder = new MediaRecorder(stream, { mimeType });
            mediaRecorderRef.current = mediaRecorder;
            
            // Clear previous chunks
            chunksRef.current = [];
            
            // Setup data handling
            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunksRef.current.push(e.data);
                    // Add chunk to audio manager
                    audioChunkManager.addChunk(e.data);
                }
            };
            
            // Start the session in the audio chunk manager
            audioChunkManager.startRecording(sessionId);
            
            // Start recording
            mediaRecorder.start(1000); // 1 second chunks
            setRecording(true);
            setDuration(0);
            
            // Setup timer for duration tracking
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
            
            timerRef.current = window.setInterval(() => {
                setDuration(prev => prev + 1);
            }, 1000);
            
        } catch (err) {
            console.error("Error starting recording:", err);
            setError("Failed to start recording. Please check your microphone permissions.");
            setStreamStatus('error');
        } finally {
            setIsButtonBusy(false);
        }
    };

    const stopRecording = async () => {
        const mediaRecorder = mediaRecorderRef.current;
        if (!mediaRecorder || mediaRecorder.state === 'inactive') {
            return;
        }
        
        setIsButtonBusy(true);
        setProcessingState('processing');
        
        try {
            // Stop timer
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            
            // Create a promise to handle stop event
            const recordingComplete = new Promise<void>((resolve) => {
                mediaRecorder.onstop = async () => {
                    try {
                        // Stop all tracks
                        if (streamRef.current) {
                            streamRef.current.getTracks().forEach(track => track.stop());
                        }
                        streamRef.current = null;
                        
                        // Get the final blob from audio chunk manager
                        const finalBlob = await audioChunkManager.stopRecording();
                        if (finalBlob) {
                            setRecordedBlob(finalBlob);
                            props.onRecordingComplete(finalBlob);
                        } else {
                            // Fallback to local chunks if audio chunk manager fails
                            const chunks = chunksRef.current;
                            if (chunks.length === 0) {
                                setError("No audio data was recorded.");
                                resolve();
                                return;
                            }
                            
                            const mimeType = mediaRecorder.mimeType || getMimeType() || "audio/webm";
                            let finalBlob = new Blob(chunks, { type: mimeType });
                            
                            // Fix duration metadata for webm files
                            if (mimeType.includes("webm")) {
                                try {
                                    finalBlob = await webmFixDuration(finalBlob, duration);
                                } catch (err) {
                                    console.warn("Could not fix webm duration metadata:", err);
                                }
                            }
                            
                            setRecordedBlob(finalBlob);
                            props.onRecordingComplete(finalBlob);
                        }
                        
                        // Clear the session
                        await audioChunkManager.clearSession();
                        setProcessingState('complete');
                        resolve();
                    } catch (err) {
                        console.error("Error processing recording:", err);
                        setError("Failed to process recording.");
                        setProcessingState('idle');
                        resolve();
                    }
                };
            });
            
            // Stop the recorder
            mediaRecorder.stop();
            setRecording(false);
            
            // Wait for processing to complete
            await recordingComplete;
        } catch (err) {
            console.error("Error stopping recording:", err);
            setError("Failed to stop recording properly.");
            setProcessingState('idle');
        } finally {
            setIsButtonBusy(false);
            cleanupAudioContext();
            setStreamStatus('inactive');
        }
    };

    const handleCloseMicModal = () => {
        if (micPermission === false) {
            setShowMicModal(false);
        } else {
            initializeMic();
        }
    };

    // Render a dynamic "Waiting for audio input" state
    const renderWaitingState = () => {
        if (recording || recordedBlob || isVisualizerReady) return null;
        
        return (
            <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
                <div className="mb-3 animate-pulse">
                    <svg className="w-10 h-10 text-primary/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                            d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                </div>
                <p className="text-text-secondary text-base font-medium">Waiting for audio input...</p>
                <p className="text-text-tertiary text-sm mt-1">Click "Start Recording" to begin</p>
            </div>
        );
    };

    // Render a processing indicator
    const renderProcessingIndicator = () => {
        if (processingState !== 'processing') return null;
        
        return (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 backdrop-blur-sm z-20 rounded-xl">
                <div className="flex flex-col items-center p-4 bg-surface dark:bg-gray-800 rounded-lg shadow-lg">
                    <div className="relative mb-3">
                        <svg className="animate-spin h-10 w-10 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    </div>
                    <p className="text-text-primary font-medium">Processing Audio</p>
                    <p className="text-text-secondary text-sm mt-1">Please wait...</p>
                </div>
            </div>
        );
    };

    return (
        <div className="w-full">
            {/* Microphone permission modal */}
            <MicrophoneModal
                isOpen={showMicModal && micPermission === false}
                onClose={handleCloseMicModal}
                isInitializing={isInitializing}
                hasPermission={micPermission}
                onRetry={initializeMic}
            />
            
            {/* Page refresh warning modal */}
            <RefreshWarningModal
                isOpen={showRefreshWarning}
                onClose={() => setShowRefreshWarning(false)}
                onConfirm={handleRefreshConfirm}
                type="recording"
            />
            
            {/* Error message */}
            {error && (
                <div className="mb-4 p-3 rounded-lg bg-error/10 border border-error/20 text-error text-sm">
                    <div className="flex items-start">
                        <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{error}</span>
                    </div>
                </div>
            )}
            
            {/* Audio visualizer container */}
            <div className="relative mb-4 bg-surface dark:bg-gray-800 rounded-xl overflow-hidden shadow-md border border-gray-200 dark:border-gray-700 aspect-[4/1] max-h-[140px]">
                <AudioVisualizer
                    audioRef={audioRef}
                    isPlaying={recording}
                    analyserNode={audioContextStateRef.current.analyzer || undefined}
                    className="h-full"
                />
                
                {/* Waiting for audio state */}
                {renderWaitingState()}
                
                {/* Processing indicator */}
                {renderProcessingIndicator()}
                
                {/* Audio level indicator */}
                {recording && (
                    <div className="absolute top-2 right-2 flex items-center bg-gray-900/70 backdrop-blur-sm px-2 py-1 rounded-full">
                        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse mr-2"></div>
                        <div className="flex space-x-0.5 h-3">
                            {[...Array(5)].map((_, i) => {
                                const threshold = i * 20;
                                const isActive = audioLevel >= threshold;
                                return (
                                    <div 
                                        key={i} 
                                        className={`w-1 rounded-sm ${isActive ? 'bg-primary' : 'bg-gray-500 bg-opacity-40'}`}
                                        style={{
                                            height: `${Math.min(100, Math.max(30, 30 + i * 15))}%`,
                                            transition: 'background-color 0.1s ease'
                                        }}
                                    ></div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
            
            {/* Recording duration display */}
            {(recording || recordedBlob) && (
                <div className="mb-4 text-center">
                    <div className="inline-flex items-center justify-center px-4 py-2 rounded-full bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light text-base font-medium">
                        {recording ? (
                            <div className="flex items-center">
                                <div className="w-2.5 h-2.5 rounded-full bg-error mr-2.5 animate-pulse shadow-lg shadow-error/30"></div>
                                <span>{formatAudioTimestamp(duration)}</span>
                            </div>
                        ) : (
                            <div className="flex items-center">
                                <svg className="w-4 h-4 mr-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>{formatAudioTimestamp(duration)}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
            
            {/* Recording controls */}
            <div className="flex flex-wrap justify-center gap-4">
                {!recording && !recordedBlob && (
                    <button
                        onClick={startRecording}
                        disabled={isButtonBusy || micPermission === false || isInitializing}
                        className={`flex items-center px-6 py-4 rounded-xl bg-primary hover:bg-primary-dark text-white font-medium transition-all duration-200 shadow-md hover:shadow-lg min-h-[var(--mobile-touch-target-size)] min-w-[200px] relative overflow-hidden group ${isButtonBusy ? 'opacity-70 cursor-not-allowed' : ''}`}
                        aria-label="Start Recording"
                    >
                        {/* Touch ripple effect */}
                        <span className="absolute inset-0 pointer-events-none touch-none opacity-0 group-active:opacity-100 group-active:animate-touch-ripple bg-white/20 rounded-full scale-0 origin-center"></span>
                        
                        {isButtonBusy ? (
                            <div className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Starting...</span>
                            </div>
                        ) : (
                            <div className="flex items-center">
                                <svg className="w-6 h-6 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                </svg>
                                <span className="text-base">Start Recording</span>
                            </div>
                        )}
                    </button>
                )}
                
                {recording && (
                    <button
                        onClick={stopRecording}
                        disabled={isButtonBusy}
                        className={`group flex items-center px-6 py-4 rounded-xl bg-error hover:bg-error/90 active:bg-error/80 text-white font-medium transition-all duration-200 shadow-md hover:shadow-lg min-h-[var(--mobile-touch-target-size)] min-w-[200px] relative overflow-hidden ${isButtonBusy ? 'opacity-70 cursor-not-allowed' : ''}`}
                        aria-label="Stop Recording"
                    >
                        {/* Touch ripple effect */}
                        <span className="absolute inset-0 pointer-events-none touch-none opacity-0 group-active:opacity-100 group-active:animate-touch-ripple bg-white/20 rounded-full scale-0 origin-center"></span>
                        
                        {isButtonBusy ? (
                            <div className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Stopping...</span>
                            </div>
                        ) : (
                            <div className="flex items-center">
                                <div className="relative mr-3 flex-shrink-0">
                                    <div className="w-6 h-6 rounded-sm bg-white group-hover:scale-90 transition-transform"></div>
                                </div>
                                <span className="text-base">Stop Recording</span>
                            </div>
                        )}
                    </button>
                )}
                
                {recordedBlob && !recording && (
                    <div className="flex flex-wrap justify-center gap-4 w-full">
                        <button
                            onClick={() => {
                                setRecordedBlob(null);
                                setDuration(0);
                            }}
                            className="flex items-center px-5 py-3 rounded-xl bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-white font-medium transition-all duration-200 shadow-sm hover:shadow-md min-h-[var(--mobile-touch-target-size)]"
                            aria-label="Discard recording"
                        >
                            <div className="flex items-center">
                                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                <span>Discard</span>
                            </div>
                        </button>
                        <button
                            onClick={startRecording}
                            disabled={isButtonBusy}
                            className={`flex items-center px-5 py-3 rounded-xl bg-primary hover:bg-primary-dark text-white font-medium transition-all duration-200 shadow-md hover:shadow-lg min-h-[var(--mobile-touch-target-size)] ${isButtonBusy ? 'opacity-70 cursor-not-allowed' : ''}`}
                            aria-label="Record new audio"
                        >
                            <div className="flex items-center">
                                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                </svg>
                                <span>Record New</span>
                            </div>
                        </button>
                    </div>
                )}
            </div>
            
            {/* Audio playback (hidden) */}
            {recordedBlob && (
                <audio 
                    ref={audioRef}
                    src={URL.createObjectURL(recordedBlob)}
                    className="hidden"
                />
            )}
        </div>
    );
}
